import { parseApiUrlHotspotContent } from "../../../api/cameraHotspots.ts";
import { resolvePatrolMediaUrl } from "./patrolMediaUrls";

/** Supported `show_type` values for asset hotspot content. */
export const PATROL_ASSET_HOTSPOT_SHOW_TYPES = [
  "image",
  "pdf",
  "website",
  "youtube",
  "camera",
  "text",
  "api_url",
];

const SHOW_TYPE_LABELS = {
  image: "Image",
  pdf: "PDF",
  website: "Website",
  youtube: "YouTube",
  camera: "Camera",
  text: "Text",
  api_url: "API URL",
};

export function getPatrolAssetHotspotShowTypeLabel(showType) {
  return SHOW_TYPE_LABELS[showType] || "Content";
}

/** Content types that open a floating panel anchored to the hotspot marker. */
export function patrolHotspotUsesAnchoredPopup(showType) {
  const t = String(showType ?? "")
    .trim()
    .toLowerCase();
  return (
    t !== "api_url" &&
    t !== "apiurl" &&
    t !== "api" &&
    t !== "camera" &&
    t !== "cam"
  );
}

export function resolveHotspotContentUrl(url) {
  if (url == null || url === "") return "";
  const s = String(url).trim();
  if (!s) return "";
  if (/^(https?:|blob:|data:)/i.test(s)) return s;
  return resolvePatrolMediaUrl(s);
}

/** Ensure website hotspots have a valid http(s) URL for open-in-tab. */
export function normalizeWebsiteHotspotUrl(url) {
  const s = String(url ?? "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s.replace(/^\/+/, "")}`;
}

/** Hostname from a website hotspot URL (lowercase), or "" if invalid. */
export function getWebsiteHotspotHostname(url) {
  try {
    return new URL(normalizeWebsiteHotspotUrl(url)).hostname.toLowerCase();
  } catch {
    return "";
  }
}

/**
 * Major sites that send X-Frame-Options / CSP frame-ancestors and cannot load in an iframe.
 * Matching is exact host or subdomain (e.g. www.google.com → google.com).
 */
const IFRAME_EMBED_BLOCKED_HOST_SUFFIXES = [
  "google.com",
  "facebook.com",
  "fb.com",
  "instagram.com",
  "twitter.com",
  "x.com",
  "linkedin.com",
  "amazon.com",
  "netflix.com",
  "apple.com",
  "microsoft.com",
  "office.com",
  "dropbox.com",
  "whatsapp.com",
  "reddit.com",
  "paypal.com",
  "github.com",
];

/** @param {string} url */
export function isWebsiteBlockedInIframe(url) {
  const host = getWebsiteHotspotHostname(url);
  if (!host) return false;
  return IFRAME_EMBED_BLOCKED_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`),
  );
}

/**
 * After iframe load, detect browser "blocked frame" placeholder (Chrome: chrome-error://).
 * @param {HTMLIFrameElement | null} iframeEl
 */
export function isIframeLoadBlockedErrorFrame(iframeEl) {
  if (!iframeEl) return false;
  try {
    const href = String(iframeEl.contentWindow?.location?.href ?? "");
    return (
      href.startsWith("chrome-error:") ||
      href.startsWith("about:blank#blocked") ||
      href.includes("chromewebdata")
    );
  } catch {
    return false;
  }
}

/**
 * @param {unknown} raw
 * @param {{ imageUrl?: string, contentUrl?: string }} hints
 */
export function normalizePatrolAssetHotspotShowType(raw, hints = {}) {
  const imageUrl = hints.imageUrl || "";
  const contentUrl = hints.contentUrl || "";
  const t = String(raw ?? "")
    .trim()
    .toLowerCase();

  if (t === "pdf" || t === "document") return "pdf";
  if (t === "youtube" || t === "yt" || t === "video") return "youtube";
  if (t === "website" || t === "link" || t === "url" || t === "web") {
    return "website";
  }
  if (t === "image" || t === "img" || t === "photo") return "image";
  if (t === "camera" || t === "cam") return "camera";
  if (t === "text" || t === "txt" || t === "plain") return "text";
  if (t === "api_url" || t === "api" || t === "apiurl") return "api_url";

  const probeContent = String(contentUrl ?? "").trim();
  if (
    probeContent.startsWith("{") &&
    (probeContent.includes('"green"') || probeContent.includes('"red"'))
  ) {
    return "api_url";
  }

  const probe = contentUrl || imageUrl;
  if (probe) {
    if (/\.pdf(\?|#|$)/i.test(probe)) return "pdf";
    if (/youtube\.com|youtu\.be/i.test(probe)) return "youtube";
    if (/\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(probe)) return "image";
    if (/^https?:\/\//i.test(probe) && !imageUrl) return "website";
  }

  if (imageUrl) return "image";
  if (contentUrl && !/^https?:\/\//i.test(contentUrl)) return "text";
  if (contentUrl) return "website";
  return "image";
}

/** @param {string} url */
/** Pretty-print JSON API bodies for patrol hotspot popups. */
export function formatPatrolHotspotApiResponseBody(text, contentType = "") {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "";
  const ct = String(contentType ?? "").toLowerCase();
  const looksJson =
    ct.includes("json") ||
    trimmed.startsWith("{") ||
    trimmed.startsWith("[");
  if (looksJson) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

export function getYoutubeEmbedUrl(url) {
  if (!url) return "";
  const s = String(url).trim();
  let id = "";
  try {
    const u = new URL(s);
    if (u.hostname.includes("youtu.be")) {
      id = u.pathname.replace(/^\//, "").split("/")[0];
    } else if (u.hostname.includes("youtube.com")) {
      id = u.searchParams.get("v") || "";
      if (!id && u.pathname.includes("/embed/")) {
        id = u.pathname.split("/embed/")[1]?.split(/[/?#]/)[0] || "";
      }
      if (!id && u.pathname.includes("/shorts/")) {
        id = u.pathname.split("/shorts/")[1]?.split(/[/?#]/)[0] || "";
      }
    }
  } catch {
    const m =
      s.match(/youtu\.be\/([^?&#]+)/i) ||
      s.match(/[?&]v=([^?&#]+)/i) ||
      s.match(/embed\/([^?&#]+)/i);
    id = m?.[1] || "";
  }
  return id ? `https://www.youtube.com/embed/${id}` : "";
}

export function hotspotHasOpenableContent(hotspot) {
  if (!hotspot) return false;
  const showType = String(hotspot.showType ?? hotspot.show_type ?? "")
    .trim()
    .toLowerCase();
  if (showType === "api_url" || showType === "apiurl" || showType === "api") {
    const { green, red, yellow } = parseApiUrlHotspotContent(
      hotspot.contentUrl ?? hotspot.content_url ?? "",
    );
    return Boolean(green || red || yellow);
  }
  if (showType === "camera" || showType === "cam") {
    return Boolean(
      String(
        hotspot.linkedCameraId ??
          hotspot.linked_camera_id ??
          hotspot.contentUrl ??
          hotspot.content_url ??
          "",
      ).trim(),
    );
  }
  const url =
    hotspot.contentUrl ||
    hotspot.imageUrl ||
    (hotspot.showType === "image" ? hotspot.previewImageUrl : "");
  return Boolean(String(url || "").trim());
}
